import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppToken } from './models/app-token';
import { config } from 'rxjs';

// This represents our raw config from the server.
// We could define a strongly-typed config class
// that wraps the values from the server, but for
// this simple demo, we won't bother with that.
export declare interface AppConfig { [key: string]: { [key: string]: any } }

@Injectable({ providedIn: 'root' })
export class AppConfigProvider {
  private config: AppConfig;

  constructor(
    private readonly httpClient: HttpClient) { }

  loadConfig(): Promise<any> {
    if (this.config) {
      return Promise.resolve(this.config);
    }
    const promise = this.httpClient.post<AppConfig>('/api/o/config', {})
      .toPromise()
      .then(configUpdated => {
        this.config = configUpdated;
        return config;
      });
    return promise;
  }

  squidexAppIsCreated(): boolean {
    if (this.config) {
      return this.config.tenantData.isSquidexAppCreated as boolean;
    }
    throw (Error('app config is undefined'));
  }

  authIsEnabled(): boolean {
    if (this.config && typeof this.config.tenantData.authIsEnabled === "boolean") {
      return <boolean>this.config.tenantData.authIsEnabled;
    }

    throw (Error('app config is undefined'));
  }

  publishVersion(): string {
    if (this.config && this.config.goLive.version) {
      return this.config.goLive.version;
    }

    throw (Error('golive version is undefined in appsetting'));
  }

  publishDate(): string {
    if (this.config && this.config.goLive.date) {
      return this.config.goLive.date;
    }

    throw (Error('golive date is undefined in appsetting'));
  }


  squidexDefaultSchemaIsCreated(): boolean {
    if (this.config) {
      return this.squidexAppIsCreated() && this.config.tenantData.isSquidexDefaultSchemaCreated as boolean;
    }
    throw (Error('app config is undefined'));
  }

  getAvailableLanguagesNames(): {} {
    const langNames = {};
    langNames[AppToken.ENGLISH_CULTURE_CODE] = 'English';
    langNames[AppToken.FRENCH_CULTURE_CODE] = 'Français';
    return langNames;
  }

  getAvailableLanguages(): string[] {
    return [AppToken.ENGLISH_CULTURE_CODE, AppToken.FRENCH_CULTURE_CODE];
  }

  getInfoTenantNotifications(languageCode: string): any {
    if (this.config && this.config.tenantNotificationData.infoNotification) {
      if (languageCode === AppToken.FRENCH_CULTURE_CODE) {
        return this.config.tenantNotificationData.infoNotification.tenantNotifications_fr;
      } else if (languageCode === AppToken.ENGLISH_CULTURE_CODE) {
        return this.config.tenantNotificationData.infoNotification.tenantNotifications_en;
      }
    }
    return null;
  }

  getAlertTenantNotifications(languageCode: string): any {
    if (this.config && this.config.tenantNotificationData.alertNotification) {
      if (languageCode === AppToken.FRENCH_CULTURE_CODE) {
        return this.config.tenantNotificationData.alertNotification.tenantNotifications_fr;
      } else if (languageCode === AppToken.ENGLISH_CULTURE_CODE) {
        return this.config.tenantNotificationData.alertNotification.tenantNotifications_en;
      }
    }
    return null;
  }

  getAccessTokenGoogleAnalytics() {
    if (this.config && this.config.googleAnalyticAccessToken.result) {
      return this.config.googleAnalyticAccessToken;
    }
    return null;
    //throw (Error('googleAnalyticAccessToken is undefined'));
  }

  getDefaultCulture(): string {
    if (this.config) {
      return this.config.tenantData.defaultCulture;
    }
    throw (Error('app config is undefined'));
  }

  getBearerToken(): string {
    if (this.config) {
      return this.config.tenantData.bearerToken;
    }
    throw (Error('app config is undefined'));
  }

  getApiEndpoint(): string {
    if (this.config) {
      return this.config.tenantData.endPoint;
    }
    throw (Error('app config is undefined'));
  }

  getEndpointAppName(): string {
    if (this.config) {
      return this.config.tenantData.endPointAppName;
    }
    throw (Error('app config is undefined'));
  }

  getAssetsBaseUrl(): string {
    return `${this.getApiEndpoint().toLowerCase()}/api/apps/${this.getEndpointAppName().toLowerCase()}/assets`;
  }

  getContentBaseUrl(): string {
    return `${this.getApiEndpoint().toLowerCase()}/api/content/${this.getEndpointAppName().toLowerCase()}`;
  }

  // Asset path when updating
  getSchemaBaseUrl(): string {
    return `${this.getApiEndpoint().toLowerCase()}/api/apps/${this.getEndpointAppName().toLowerCase()}`;
  }

  // Asset path when displaying in HTML page
  getAssetsPathUrl(): string {
    return `${this.getApiEndpoint().toLowerCase()}/api/assets/${this.getEndpointAppName().toLowerCase()}`;
  }

  // All languages path
  getLanguagePathUrl(): string {
    return `${this.getApiEndpoint().toLowerCase()}/api/languages/`;
  }


  // Roles path
  getRolesPathUrl(): string {
    return `${this.getApiEndpoint().toLowerCase()}/api/apps/${this.getEndpointAppName().toLowerCase()}/roles/`;
  }


}
